import { parse } from "qs";
import findLast from "lodash/findLast";
import { mapOcupanciesToPassengers } from "app/utils/smartDPUtils";
import { PASSENGER_TYPE } from "app/constants";

export const extractOccupanciesAndPassengers = search => {
	const queryParams = parse(search, { ignoreQueryPrefix: true });

	const newOccupancies = queryParams.occupancies?.map(occupancy => {
		const childrenBirthdates =
			occupancy.childrenBirthdates?.map(childrenBirthdate => ({
				day:
					childrenBirthdate?.day || childrenBirthdate?.day === 0
						? Number(childrenBirthdate.day)
						: undefined,
				month:
					childrenBirthdate?.month || childrenBirthdate?.month === 0
						? Number(childrenBirthdate.month)
						: undefined,
				year:
					childrenBirthdate?.year || childrenBirthdate?.year === 0
						? Number(childrenBirthdate.year)
						: undefined,
			})) || [];

		const adults = occupancy?.adults ? Number(occupancy.adults) : 0;
		const children = occupancy?.children ? Number(occupancy.children) : 0;

		return {
			adults,
			children,
			childrenBirthdates: childrenBirthdates.length > 0 ? childrenBirthdates : undefined,
		};
	});

	const passengers = mapOcupanciesToPassengers(newOccupancies || []);
	const adults = passengers.filter(passenger => passenger.type === PASSENGER_TYPE.ADULT) || [];
	const children = passengers.filter(passenger => passenger.type === PASSENGER_TYPE.CHILD) || [];

	return {
		newOccupancies,
		adults,
		children,
	};
};

export const extractDestinationAndBooking = ({
	queryParams,
	destinations,
	newOccupancies,
	adults,
	children,
	offerType = "",
}) => {
	let destinationData = {};

	if (queryParams.destinationIATA) {
		destinationData = destinations.find(destination =>
			destination?.airportIATAs
				?.map(iata => iata?.name)
				?.includes(queryParams.destinationIATA)
		);
	} else {
		destinationData = destinations.find(
			destination => String(destination.code) === queryParams.destinationResortCode
		);
	}

	const bookingToSave = {
		destinationResort: {
			code: String(destinationData?.code),
			label: destinationData?.label,
			type: destinationData?.type,
		},
		departureDate: Number(queryParams.departureDate),
		endDate: Number(queryParams.endDate),
		duration: {
			value: Number(queryParams.duration),
		},
		occupancies: newOccupancies,
		passengers: [...adults, ...children],
		adults: adults.length || 2,
		children: children.length,
		...(offerType.length > 0 && { offer: { type: offerType } }),
	};

	return { bookingToSave, destinationData };
};

// Shared logic to pick the default checked baggage: use included, otherwise the last free option, otherwise current/base.
export const selectDefaultFlightBaggage = ({
	flightOptions = {},
	passengersCount = 0,
	currentLuggage = {},
} = {}) => {
	const baseOption = flightOptions.flightBagageOption || {};
	const availableOptions = flightOptions.flightBagageOptions || [];
	const passengers = Number(passengersCount) || 0;
	const currentQuantity = currentLuggage.quantity;
	const selectedQuantity = Number(currentLuggage.quantity ?? 0);
	const weightFrom = option => option?.maxWeightPerBaggage ?? option?.maxWeightPerBagage;
	const baseWeight = weightFrom(baseOption) ?? currentLuggage.maxWeightPerBaggage;

	const hasPricePerBooking =
		typeof baseOption.pricePerBooking === "number" && baseOption.pricePerBooking >= 0;
	const isIncluded = baseOption.included === true;
	const canBeBooked = baseOption.canBeBooked === true;

	const freeOption = findLast(availableOptions, option => option?.pricePerBooking === 0);

	if (isIncluded || (hasPricePerBooking && passengers > 0 && selectedQuantity !== passengers)) {
		return { quantity: 0, maxWeightPerBaggage: baseWeight };
	}

	if (freeOption && canBeBooked && !isIncluded) {
		return {
			quantity: freeOption.quantity,
			maxWeightPerBaggage: weightFrom(freeOption) ?? baseWeight,
		};
	}

	if (currentLuggage.quantity !== undefined || currentLuggage.maxWeightPerBaggage !== undefined) {
		return {
			quantity: currentQuantity,
			maxWeightPerBaggage: currentLuggage.maxWeightPerBaggage ?? baseWeight,
		};
	}

	return { quantity: baseOption.quantity, maxWeightPerBaggage: baseWeight };
};
